import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "",
  {
    variants: {
      variant: {
        primary: [
          "rounded font-sans font-medium transition-colors text-white",
          "bg-black-800",
          "hover:bg-black-700",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black-600 focus-visible:ring-offset-2",
          "disabled:bg-black-400 disabled:text-white disabled:cursor-not-allowed",
        ],
        secondary: [
          "rounded font-sans font-medium transition-colors bg-white border border-black-800",
          "text-black-800",
          "hover:bg-beige-200 hover:text-black-800",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black-600 focus-visible:ring-offset-2",
          "disabled:border-black-400 disabled:text-black-400 disabled:cursor-not-allowed",
        ],
        ghost: [
          "rounded font-sans font-medium transition-colors",
          "text-black-800",
          "hover:bg-beige-100 hover:text-black-800",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black-600 focus-visible:ring-offset-2",
          "disabled:text-black-500 disabled:cursor-not-allowed",
        ],
        destructive: [
          "rounded font-sans font-medium transition-colors text-white",
          "bg-semantic-error-500",
          "hover:bg-semantic-error-700",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-semantic-error-700 focus-visible:ring-offset-2",
          "disabled:bg-semantic-error-300 disabled:text-white disabled:cursor-not-allowed",
        ],
        none: [],
      },
      size: {
        large: "px-4 py-2 text-t1",
        lg: "px-4 py-2 text-t1",
        medium: "px-3 py-2 text-button1",
        md: "px-3 py-2 text-button1",
        small: "px-2 py-1 text-small2",
        sm: "px-2 py-1 text-small2",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "medium",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  isCollapsed?: boolean
  isSelected?: boolean
  isLoading?: boolean
}

const IconButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, startIcon, endIcon, children, isCollapsed = false, isSelected = false, isLoading = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    // variant defaults to "primary" via cva's defaultVariants, but that
    // resolution only happens inside buttonVariants() — mirror it here too,
    // otherwise an omitted variant prop reads as undefined !== "primary"
    // and falls through to text-current, rendering invisible text on the
    // solid dark background.
    const resolvedVariant = variant ?? "primary"
    const usesLightText = resolvedVariant === "primary" || resolvedVariant === "destructive"
    // See Button: Slot must receive exactly one element child.
    if (asChild) {
      return (
        <Slot
          className={cn(
            buttonVariants({ variant, size }),
            "inline-flex items-center justify-center transition-all duration-200 overflow-hidden",
            usesLightText ? "[&_*]:text-white" : "[&_*]:text-current",
            isCollapsed ? "w-9 p-0" : "gap-2",
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      )
    }
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          "inline-flex items-center justify-center transition-all duration-200 overflow-hidden",
          usesLightText ? "[&_*]:text-white" : "[&_*]:text-current",
          isCollapsed ? "w-9 p-0" : "gap-2",
          className
        )}
        ref={ref}
        disabled={isLoading || props.disabled}
        {...props}
      >
        <div className="flex items-center justify-center min-w-4">
          {isLoading ? (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            startIcon
          )}
        </div>
        {children && (
          <span
            className={cn("transition-opacity duration-200 whitespace-nowrap", {
              "opacity-0 w-0": isCollapsed,
              "opacity-100": !isCollapsed
            })}
          >
            {children}
          </span>
        )}
        {endIcon && (
          <div className={cn("transition-opacity duration-200", {
            "opacity-0 w-0": isCollapsed,
            "opacity-100": !isCollapsed
          })}>
            {endIcon}
          </div>
        )}
      </Comp>
    )
  }
)
IconButton.displayName = "IconButton"

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading = false, startIcon, endIcon, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    // See IconButton for why this mirrors cva's defaultVariants resolution.
    const resolvedVariant = variant ?? "primary"
    const usesLightText = resolvedVariant === "primary" || resolvedVariant === "destructive"
    // Radix Slot requires exactly one element child: with asChild, render the
    // caller's child untouched (icons belong inside it) — the extra icon/text
    // wrappers below would hand Slot an array and throw React.Children.only.
    if (asChild) {
      return (
        <Slot
          className={cn(
            buttonVariants({ variant, size }),
            "inline-flex items-center justify-center gap-2 whitespace-nowrap",
            usesLightText ? "[&_*]:text-white" : "[&_*]:text-current",
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      )
    }
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          "inline-flex items-center justify-center gap-2 whitespace-nowrap",
          usesLightText ? "[&_*]:text-white" : "[&_*]:text-current"
        )}
        ref={ref}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {startIcon || isLoading ? (
          <span className="mr-2 inline-flex items-center">
            {isLoading ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              startIcon
            )}
          </span>
        ) : null}
        {children ? (
          <span className="inline-flex items-center">{children}</span>
        ) : null}
        {endIcon ? (
          <span className="ml-2 inline-flex items-center">{endIcon}</span>
        ) : null}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, IconButton, buttonVariants }
